'use client';

/**
 * Module-scope event bus for cross-route flow coordination.
 *
 * Why this exists: now that flows live behind URL-routed
 * modal pages (parallel + intercepting routes), we lost the
 * ability to thread callbacks (`onDone`, `onSelect`) directly
 * to the surface. URLs can't carry functions. Instead:
 *
 *   - The trigger hook (e.g. `useCollectionsFlow().addCollection({ onDone })`)
 *     registers a **one-shot subscriber** on the bus's channel
 *     for that flow, then `router.push`es to the form route.
 *   - The flow's modal surface, on save, fires `emit(channel, payload)`.
 *   - Every subscriber on the channel runs once and unsubscribes.
 *
 * Two flavors of subscriber:
 *
 *   - `subscribe(channel, fn)`: persistent. Used by the
 *     manage-list modal — it stays subscribed for its whole
 *     mount so any `created` / `updated` event refreshes it.
 *
 *   - `subscribeOnce(channel, fn)`: fires once, then
 *     auto-unsubscribes. Used by callers that pass `onDone` /
 *     `onSelect` — fire-and-forget callback semantics.
 *
 * Lost on browser refresh by design — same as the legacy
 * in-memory callback system. Refresh = no callback fires =
 * user just sees the URL state and works from there.
 */

type Listener<TPayload> = (payload: TPayload) => void;

const channels = new Map<string, Set<Listener<unknown>>>();

function getOrCreate(channel: string): Set<Listener<unknown>> {
  let set = channels.get(channel);
  if (!set) {
    set = new Set();
    channels.set(channel, set);
  }
  return set;
}

/** Subscribe persistently. Returns an unsubscribe function. */
export function subscribe<TPayload>(
  channel: string,
  fn: Listener<TPayload>,
): () => void {
  const set = getOrCreate(channel);
  set.add(fn as Listener<unknown>);
  return () => {
    set.delete(fn as Listener<unknown>);
  };
}

/** Subscribe once. Auto-unsubscribes after the first emit. */
export function subscribeOnce<TPayload>(
  channel: string,
  fn: Listener<TPayload>,
): () => void {
  const wrapped = (payload: unknown) => {
    unsubscribe();
    fn(payload as TPayload);
  };
  const set = getOrCreate(channel);
  set.add(wrapped);
  const unsubscribe = () => {
    set.delete(wrapped);
  };
  return unsubscribe;
}

/** Emit a payload to every subscriber. */
export function emit<TPayload>(channel: string, payload: TPayload): void {
  const set = channels.get(channel);
  if (!set) return;
  // Snapshot before iterating: subscribeOnce mutates the set
  // during iteration (the once-listener removes itself), and
  // a plain forEach over a mutating Set skips entries.
  const snapshot = Array.from(set);
  for (const listener of snapshot) {
    try {
      (listener as Listener<TPayload>)(payload);
    } catch (err) {
      console.error(`[flow-events] listener on "${channel}" threw:`, err);
    }
  }
}

// ─── Channels ──────────────────────────────────────────────
// Centralized channel names so subscribers + emitters can't
// drift. Add new channels here as flows port to URL routes.

export const FlowChannels = {
  /** The sidebar's Email row asks the dashboard chrome to open the
   *  Google connect prompt. A bus event (not a route) because the row is
   *  static config with no way to reach React state. */
  GoogleConnectRequested: 'google.connect-requested',
  /** The sidebar's "Content Creation Agent" row asks the dashboard chrome to
   *  open the Content Creation chooser modal (AI PDF Creator / AI Creator
   *  Studio). A bus event (not a route) for the same reason as
   *  `GoogleConnectRequested` — the row is static config with no React state. */
  ContentCreationRequested: 'content-creation.requested',
  /** The real-estate sidebar's Guides row asks the dashboard chrome to open
   *  the Guides chooser (Create with AI / Manage Guides). A bus event for the
   *  same reason as `ContentCreationRequested`. */
  GuidesChooserRequested: 'guides-chooser.requested',
  CollectionsSaved: 'collections.saved',
  CollectionsDeleted: 'collections.deleted',
  SeriesSaved: 'series.saved',
  SeriesDeleted: 'series.deleted',
  SeriesPicked: 'series.picked',
  PostsSaved: 'posts.saved',
  PostsDeleted: 'posts.deleted',
  CourseSaved: 'course.saved',
  CourseDeleted: 'course.deleted',
  /** Fires after the creator saves their contact-card payload.
   *  Surfaces that snapshot `store.contactCard` (the bio header
   *  pill, the dashboard preview tile) listen so they can pull
   *  the fresh values without a full page reload. */
  ContactCardSaved: 'contact-card.saved',
  /** Fires whenever a product (store listing) is created /
   *  updated / archived / deleted. 1:1 with legacy
   *  `productEvent$`. Surfaces that show a product list (My
   *  Products container, Recently Added rail, Add-to-Collection
   *  picker) listen so they refetch after the creator mutates
   *  the catalog. */
  ProductsCreated: 'products.created',
  ProductsUpdated: 'products.updated',
  ProductsDeleted: 'products.deleted',
  ProductsArchived: 'products.archived',
  DigitalProductSaved: 'digital-product.saved',
  /** Fires after a clip is uploaded / linked into a series.
   *  Surfaces that show series elements (My Life tab series
   *  rails, CollectionButton's `SeriesElementsFetcher`,
   *  series detail page) listen so the new clip appears
   *  without a manual refresh. 1:1 with legacy `clipEvent$`. */
  ClipsSaved: 'clips.saved',
  /** Fires after a clip's metadata is edited (title, description,
   *  access level, thumbnail, tagged products / links). Mirror of
   *  legacy `popClipEvent$.next({ type: 'updated' })`. Series
   *  rails / All Videos / chapter lists listen so they refetch
   *  the clip's row. */
  ClipsUpdated: 'clips.updated',
  /** Fires after a clip is hard-deleted (removed from the
   *  store everywhere — cascades through every series the
   *  clip was a member of). Mirror of legacy
   *  `popClipEvent$.next({ type: 'deleted' })`. */
  ClipsDeleted: 'clips.deleted',
  /** Fires after a clip is removed from a SINGLE series — the
   *  clip itself stays in the store. Mirror of legacy
   *  `popClipEvent$.next({ type: 'removed-from-series' })`.
   *  Payload carries `{ clipId, seriesId }`. */
  ClipsRemovedFromSeries: 'clips.removed-from-series',
  /** Fires after the creator saves their calendar availability /
   *  meeting types (the `/my-calendar/setup` surface). The calendar
   *  view listens so it refetches availability + events when the
   *  setup drawer closes, without a manual reload. */
  AvailabilitySaved: 'availability.saved',
  /** Fires after a visitor completes a meeting booking (the meeting checkout
   *  succeeds). The bio calendar button listens so it refetches available dates
   *  + meeting types/slots, dropping the just-booked slot — the booking checkout
   *  is a soft nav that keeps the calendar mounted underneath, so nothing
   *  remounts and this explicit signal is what refreshes it (RN `onDone`). */
  MeetingBooked: 'meeting.booked',
  /** Fires after an upcoming show ("live preview") is created or
   *  edited. The dashboard upcoming-show banner listens so it refetches
   *  the soonest show without a manual reload. */
  ShowsSaved: 'shows.saved',
  /** Fires after a scheduled show is cancelled/deleted. The banner
   *  listens so it drops the show. */
  ShowsDeleted: 'shows.deleted',
  /** Fires after the creator adds / updates / cancels a tipping goal.
   *  The visitor-bio entity-cache watcher listens so it busts the
   *  creator's `tipping-goal` cache tag. Payload carries `{ id }`. */
  TippingGoalSaved: 'tipping-goal.saved',
  /** Fires the moment the OAuth popup returns a `code` and the exchange
   *  (`authorizeGoogleCalendar`) STARTS — before it resolves. Lets a resume
   *  surface show a "connecting…" processing state during the network call, so
   *  the gap between the popup closing and the success modal isn't a
   *  feedback-less pause. Payload is the same parsed OAuth state. */
  GoogleCalendarConnecting: 'google-calendar.connecting',
  /** Fires after the creator's Google Calendar OAuth code is exchanged
   *  successfully (`GoogleCalendarCallbackHandler`). Payload carries the
   *  parsed OAuth `state` (`source`/`module`/`action`/`extra`) so a listener
   *  can decide whether to resume into a destination — the `MeetingSetupProvider`
   *  listens to show the "Connected!" modal and route to `/my-calendar/setup`
   *  when the connect began from the calendar flow. 1:1 with RN's
   *  `googleCalendarConnected$.next(state)`. */
  GoogleCalendarConnected: 'google-calendar.connected',
  /** Fires if the code exchange FAILS (after `GoogleCalendarConnecting`), so a
   *  listener can drop the "connecting…" processing state. Payload is the
   *  parsed OAuth state. */
  GoogleCalendarConnectFailed: 'google-calendar.connect-failed',
  /** Fires after the creator saves a fan-subscription tier in the
   *  Configure Subscriptions surface (`ManageSubscriptionModal` — any
   *  host: `/manage-subscription` route, Home quick settings, …).
   *  Subscription consumers listen so their tier lists update live:
   *  the course form's access settings (`useFetchAllSubscriptions`)
   *  and the clip editor's tier picker (`AccessControlSection`) —
   *  the latter also auto-selects the tier the creator left to
   *  configure (legacy `accept()`). */
  SubscriptionConfigSaved: 'subscription-config.saved',
} as const;

export type FlowChannel = (typeof FlowChannels)[keyof typeof FlowChannels];
